import { useState } from "react";
import { MoreHorizontal, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useHaptics } from "@/hooks/use-haptics";
import { useRecentTools } from "@/hooks/use-recent-tools";
import { primaryNav, secondaryNav } from "@/lib/nav-items";

const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lightTap } = useHaptics();
  const { trackVisit } = useRecentTools();
  const [moreOpen, setMoreOpen] = useState(false);

  const isMoreActive = secondaryNav.some(t => t.path === location.pathname);

  return (
    <>
      {/* More Menu Overlay */}
      {moreOpen && (
        <div className="fixed inset-0 z-[60] md:hidden" onClick={() => setMoreOpen(false)}>
          <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
          <div
            className="absolute bottom-[calc(3.5rem+env(safe-area-inset-bottom))] left-2 right-2 bg-card border border-border rounded-xl shadow-lg p-2 animate-fade-in"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-3 py-1.5 mb-1">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">More Tools</span>
              <button onClick={() => setMoreOpen(false)} className="p-1 rounded-md hover:bg-secondary text-muted-foreground" aria-label="Close menu">
                <X className="w-4 h-4" />
              </button>
            </div>
            {secondaryNav.map(tab => {
              const isActive = location.pathname === tab.path;
              return (
                <button
                  key={tab.path}
                  onClick={() => {
                    lightTap();
                    trackVisit(tab.path, tab.label);
                    navigate(tab.path);
                    setMoreOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive ? "bg-primary/10 text-primary" : "text-foreground hover:bg-secondary"
                  }`}
                >
                  <tab.icon className="w-5 h-5 shrink-0" />
                  <div className="text-left">
                    <div className="text-sm font-medium">{tab.label}</div>
                    <div className="text-[11px] text-muted-foreground">{tab.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50 md:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} aria-label="Main navigation">
        <div className="flex items-center justify-around h-14" role="tablist">
          {primaryNav.map((tab) => {
            const isActive = location.pathname === tab.path;
            const label = tab.shortLabel ?? tab.label;
            return (
              <button
                key={tab.path}
                onClick={() => { lightTap(); trackVisit(tab.path, tab.label); navigate(tab.path); setMoreOpen(false); }}
                aria-current={isActive ? "page" : undefined}
                aria-label={tab.label}
                className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="text-[10px] font-semibold uppercase tracking-wider leading-none">{label}</span>
              </button>
            );
          })}
          <button
            onClick={() => { lightTap(); setMoreOpen(prev => !prev); }}
            aria-label="More tools"
            aria-expanded={moreOpen}
            className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors ${
              isMoreActive || moreOpen ? "text-primary" : "text-muted-foreground"
            }`}
          >
            <MoreHorizontal className="w-5 h-5" />
            <span className="text-[10px] font-semibold uppercase tracking-wider leading-none">More</span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default BottomNav;
